import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Box,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Aircraft } from "../../../../types";

interface Props {
  aircraft: Aircraft[];
  completionStatus: Record<string, boolean>;
  selectedAircraft: Aircraft | null;
  onSelectAircraft: (aircraft: Aircraft, index: number) => void;
  tableRef: React.RefObject<HTMLDivElement>;
  aircraftMap: Record<string, string>;
}

type OrderDirection = "asc" | "desc";
type OrderBy = "make" | "model" | "length" | "wingspan" | "tail_height" | "status";

export const AircraftTable: React.FC<Props> = ({
  aircraft,
  completionStatus,
  selectedAircraft,
  onSelectAircraft,
  tableRef,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [orderBy, setOrderBy] = React.useState<OrderBy>("make");
  const [orderDirection, setOrderDirection] = React.useState<OrderDirection>("asc");

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getStatus = (aircraft: Aircraft): 'deployed' | 'locked' | 'labeling' => {
    if (aircraft.linked_to) {
      return 'locked';
    }
    if (completionStatus[aircraft.id]) {
      return 'deployed';
    }
    return 'labeling';
  };

  const filteredAndSortedAircraft = React.useMemo(() => {
    let filtered = aircraft.filter((a) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        a.make?.toLowerCase().includes(searchLower) ||
        a.model?.toLowerCase().includes(searchLower) ||
        a.id.toLowerCase().includes(searchLower)
      );
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case "status":
          const statusOrder = { deployed: 0, locked: 1, labeling: 2 };
          aValue = statusOrder[getStatus(a)];
          bValue = statusOrder[getStatus(b)];
          break;
        default:
          aValue = a[orderBy] || "";
          bValue = b[orderBy] || "";
      }

      if (aValue < bValue) {
        return orderDirection === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return orderDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [aircraft, completionStatus, searchTerm, orderBy, orderDirection]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search by make, model, or ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      
      <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }} ref={tableRef}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) => theme.palette.grey[300],
                '& .MuiTableCell-head': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  padding: '8px 16px',
                  whiteSpace: 'nowrap',
                  backgroundColor: (theme) => theme.palette.grey[300],
                }
              }}
            >
              <TableCell>
                <TableSortLabel
                  active={orderBy === "make"}
                  direction={orderBy === "make" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("make")}
                >
                  Make
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "model"}
                  direction={orderBy === "model" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("model")}
                >
                  Model
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "length"}
                  direction={orderBy === "length" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("length")}
                >
                  Length
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "wingspan"}
                  direction={orderBy === "wingspan" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("wingspan")}
                >
                  Wingspan
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "tail_height"}
                  direction={orderBy === "tail_height" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("tail_height")}
                >
                  Tail Height
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? orderDirection : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedAircraft.map((aircraft, index) => {
              const status = getStatus(aircraft);
              const isSelected = selectedAircraft?.id === aircraft.id;
              
              return (
                <TableRow
                  key={aircraft.id}
                  hover
                  selected={isSelected}
                  onClick={() => onSelectAircraft(aircraft, index)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{aircraft.make || "-"}</TableCell>
                  <TableCell>{aircraft.model || "-"}</TableCell>
                  <TableCell align="right">
                    {aircraft.length ? aircraft.length.toFixed(1) : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {aircraft.wingspan ? aircraft.wingspan.toFixed(1) : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {aircraft.tail_height ? aircraft.tail_height.toFixed(1) : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status === 'deployed' ? 'Deployed' : status === 'locked' ? 'Locked' : 'Labeling'}
                      color={status === 'deployed' ? 'success' : status === 'locked' ? 'warning' : 'error'}
                      size="small"
                      sx={{ minWidth: 80, width: 80 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};